import { rateSchema } from "@inrev/common";

export type BondingLineMode = "review" | "readonly" | "editing";

export type BondingLineDraft = {
	rate: number | null;
	singleLimit: number | null;
	aggregateLimit: number | null;
	match: boolean;
};

export type InrevDraft = BondingLineDraft & { isPublished: boolean };

export type BondingLineDrafts = {
	inrev: InrevDraft;
	carriers: Record<string, BondingLineDraft>;
};

export type BondingLineState = BondingLineDrafts & {
	mode: BondingLineMode;
	baseline: BondingLineDrafts;
	submitAttempted: boolean;
};

export type BondingLineTarget = { kind: "inrev" } | { kind: "carrier"; carrierName: string };

export type BondingLineAction =
	| { type: "set_rate"; target: BondingLineTarget; value: number | null }
	| { type: "set_single"; target: BondingLineTarget; value: number | null }
	| { type: "set_aggregate"; target: BondingLineTarget; value: number | null }
	| { type: "toggle_match"; carrierName: string }
	| { type: "match_all" }
	| { type: "toggle_publish" }
	| { type: "enter_edit" }
	| { type: "cancel_edit" }
	| { type: "mark_submit_attempted" }
	| { type: "reset_from_account"; drafts: BondingLineDrafts; mode: BondingLineMode };

type EditableField = "rate" | "singleLimit" | "aggregateLimit";

const cloneDrafts = (drafts: BondingLineDrafts): BondingLineDrafts => ({
	inrev: { ...drafts.inrev },
	carriers: Object.fromEntries(Object.entries(drafts.carriers).map(([k, v]) => [k, { ...v }])),
});

const setField = (
	state: BondingLineState,
	field: EditableField,
	target: BondingLineTarget,
	value: number | null,
): BondingLineState => {
	if (target.kind === "inrev") {
		const inrev = { ...state.inrev, [field]: value };
		// A $0 single limit can't be published (mirrors the backend coercion).
		if (field === "singleLimit" && (value === null || value <= 0)) inrev.isPublished = false;
		const carriers = { ...state.carriers };
		for (const [name, draft] of Object.entries(carriers)) {
			if (draft.match) carriers[name] = { ...draft, [field]: value };
		}
		return { ...state, inrev, carriers };
	}
	const existing = state.carriers[target.carrierName];
	if (existing === undefined) return state;
	return {
		...state,
		carriers: { ...state.carriers, [target.carrierName]: { ...existing, [field]: value } },
	};
};

/** A carrier draft that mirrors the inRev benchmark (rate + limits). */
const matchedDraft = (inrev: InrevDraft): BondingLineDraft => ({
	match: true,
	rate: inrev.rate,
	singleLimit: inrev.singleLimit,
	aggregateLimit: inrev.aggregateLimit,
});

export const allCarriersMatched = (state: BondingLineState): boolean => {
	const names = Object.keys(state.carriers);
	return names.length > 0 && names.every((name) => state.carriers[name].match);
};

export const bondingLineReducer = (
	state: BondingLineState,
	action: BondingLineAction,
): BondingLineState => {
	switch (action.type) {
		case "set_rate":
			return setField(state, "rate", action.target, action.value);
		case "set_single":
			return setField(state, "singleLimit", action.target, action.value);
		case "set_aggregate":
			return setField(state, "aggregateLimit", action.target, action.value);
		case "toggle_match": {
			const existing = state.carriers[action.carrierName];
			if (existing === undefined) return state;
			const next: BondingLineDraft = existing.match
				? { ...existing, match: false }
				: matchedDraft(state.inrev);
			return { ...state, carriers: { ...state.carriers, [action.carrierName]: next } };
		}
		case "match_all": {
			const names = Object.keys(state.carriers);
			if (names.length === 0) return state;
			const nextMatched = !allCarriersMatched(state);
			const carriers: Record<string, BondingLineDraft> = {};
			for (const name of names) {
				carriers[name] = nextMatched
					? matchedDraft(state.inrev)
					: { ...state.carriers[name], match: false };
			}
			return { ...state, carriers };
		}
		case "toggle_publish": {
			const nextIsPublished = !state.inrev.isPublished;
			const single = state.inrev.singleLimit;
			// A $0 single limit can't be published.
			if (nextIsPublished && (single === null || single <= 0)) return state;
			return { ...state, inrev: { ...state.inrev, isPublished: nextIsPublished } };
		}
		case "enter_edit":
			return { ...state, mode: "editing", submitAttempted: false };
		case "cancel_edit":
			return {
				...cloneDrafts(state.baseline),
				mode: "readonly",
				baseline: state.baseline,
				submitAttempted: false,
			};
		case "mark_submit_attempted":
			return { ...state, submitAttempted: true };
		case "reset_from_account": {
			const next = cloneDrafts(action.drafts);
			return {
				...next,
				mode: action.mode,
				baseline: cloneDrafts(action.drafts),
				submitAttempted: false,
			};
		}
		default:
			return state;
	}
};

const draftDirty = (a: BondingLineDraft, b: BondingLineDraft): boolean =>
	a.rate !== b.rate ||
	a.singleLimit !== b.singleLimit ||
	a.aggregateLimit !== b.aggregateLimit ||
	a.match !== b.match;

export const isStateDirty = (state: BondingLineState): boolean => {
	if (
		draftDirty(state.inrev, state.baseline.inrev) ||
		state.inrev.isPublished !== state.baseline.inrev.isPublished
	) {
		return true;
	}
	for (const name of Object.keys(state.carriers)) {
		const base = state.baseline.carriers[name];
		if (base === undefined || draftDirty(state.carriers[name], base)) return true;
	}
	return false;
};

/** Structural + value equality across both draft sets, including the inRev publish flag. */
export const draftsEqual = (a: BondingLineDrafts, b: BondingLineDrafts): boolean => {
	if (draftDirty(a.inrev, b.inrev) || a.inrev.isPublished !== b.inrev.isPublished) return false;
	const names = Object.keys(a.carriers);
	if (names.length !== Object.keys(b.carriers).length) return false;
	for (const name of names) {
		const other = b.carriers[name];
		if (other === undefined || draftDirty(a.carriers[name], other)) return false;
	}
	return true;
};

/**
 * A line is in error when:
 *  - a required field is missing,
 *  - the rate falls outside `rateSchema` (a fraction in `[0, 1)`, i.e. 0%–100%),
 *  - a limit is negative, or
 *  - the aggregate limit is below the single limit (a single bond can't exceed the total).
 *
 * A $0 single limit is valid here — it just can't be published; dropping the limit to $0
 * unpublishes the line (see `toggle_publish`/`set_single`) rather than flagging an error.
 *
 * Cell parsing only rejects non-numeric input, so out-of-range numbers reach the draft and
 * are surfaced here as a row error rather than silently reverting.
 */
export const lineHasError = (draft: BondingLineDraft): boolean => {
	const { rate, singleLimit, aggregateLimit } = draft;
	if (rate === null || singleLimit === null || aggregateLimit === null) return true;
	if (!rateSchema.safeParse(rate).success) return true;
	if (singleLimit < 0 || aggregateLimit < 0) return true;
	return aggregateLimit < singleLimit;
};

export const anyLineHasError = (state: BondingLineState): boolean =>
	lineHasError(state.inrev) || Object.values(state.carriers).some(lineHasError);

/**
 * Row errors stay hidden until the admin attempts to submit, so a freshly loaded review
 * account with empty lines doesn't show red outlines. Readonly mode never flags rows.
 */
export const shouldShowLineErrors = (state: BondingLineState): boolean =>
	state.mode !== "readonly" && state.submitAttempted;
